import * as Popover from '@radix-ui/react-popover';
import React, { useState } from 'react';

import classNames from '@utils/classnames';

import { BaseButton } from '../button/BaseButton';
import { popoverPanelClassName } from '../form/control';
import { HorizontalRule } from '../HorizontalRule';

export interface IActionsMenuProps {
  /** Accessible name and tooltip of the icon-only trigger. */
  label: string;
  /** The trigger's icon — a gear for settings, a kebab for an overflow menu. */
  icon: React.ReactElement;
  /**
   * Non-interactive content above the items, e.g. the pill saying which stored key this thing uses.
   * Clicking it leaves the menu open, unlike a click on an item.
   */
  header?: React.ReactNode;
  /** `ActionMenuItem` rows. */
  children: React.ReactNode;
}

/**
 * An icon-only trigger with a menu of actions behind it, for controls that belong to the thing they
 * sit on — a row, a card — rather than to the page, so they stay out of the way until asked for.
 *
 * The trigger is a `BaseButton` rather than `Button`: Radix's `Trigger asChild` hands the trigger its
 * own `onClick` and `aria-*`, and only `BaseButton` passes those through.
 */
export const ActionsMenu: React.FC<IActionsMenuProps> = (props) => {
  const { label, icon, header, children } = props;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <BaseButton color="ghost" size={8} iconLeft={icon} aria-label={label} title={label} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          className={classNames(popoverPanelClassName, 'w-56 p-1')}
          // Picking an item closes the menu: the action either runs here or opens a dialog, and either
          // way the menu has done its job. The item's own handler runs first, as the click bubbles.
          onClick={() => setIsOpen(false)}
        >
          {header != null && (
            <div className="px-2.5 py-1" onClick={(evt) => evt.stopPropagation()}>
              {header}
              <HorizontalRule className="mt-1.5" />
            </div>
          )}
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
